
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import { Partytown } from '@builder.io/partytown/react';
import BSpinner from 'components/common/BSpinner';
import OTPModal from 'components/common/OTPModal';
import AuthModalWrapper from 'components/Layout/Auth/AuthModalWrapper';
import Layout, { LayoutProps } from 'components/Layout/Common/Layout';
import { Locale } from 'enums/locale';
import { reportAccessibility } from 'utils/reportAccesibility';
import { useAdminCredentialByPass } from 'hooks/useAdminCredentialByPass';
import { useFirstRender } from 'hooks/useFirstRender';
import { useInterceptors } from 'hooks/useInterceptors';
import { usePrevLink } from 'hooks/usePrevLink';
import { useQueryParams } from 'hooks/useQueryParams';
import { withTranslateRoutes } from 'next-translate-routes';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import NextNProgress from 'nextjs-progressbar';
import { FunctionComponent, useEffect, useState } from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import { analyticsService } from 'services/AnalyticsService';
import { authService } from 'services/AuthService';
import { localizationService } from 'services/LocalizationService';
import { notificationService } from 'services/NotificationService';
import { searchService } from 'services/SearchService';
import { useNotificationState } from 'states/useNotificationState';
import { useOTPModalState } from 'states/useOTPModal';
import { useSearchState } from 'states/useSearchState';
import { useSpinnerState } from 'states/useSpinnerState';
import { useUserState } from 'states/useUserState';
import 'styles/globals.css';
import { SearchState } from 'types/search.type';
import { getAlternateHref } from 'utils/generateAlternateHrefs';
import 'intl-pluralrules';
import { remoteConfigService } from 'services/RemoteConfigService';
import { useRemoteConfigState } from 'states/useRemoteConfigState';
import { useComponentMounted } from 'hooks/useComponentMounted';
import { favoriteService } from 'services/FavoriteService';
import { useFavoriteStore } from 'states/useFavorite.store';
import { useBreakpoint } from 'hooks/useBreakpoints';
import { useExperienceSearchState } from 'states/useExperienceSearchState';
import getT from 'next-translate/getT';
import { locationService } from 'services/LocationService';

interface MyAppProps extends AppProps {
  searchData: SearchState[];
  experienceSearchData: SearchState[];
  remoteConfigs: Record<string, unknown>;
  userAgent: string;
  pageQueryParamMap: Record<string, string>;
}

function MyApp({
  Component,
  pageProps,
  router,
  searchData: searchDataApi,
  experienceSearchData: experienceSearchDataApi,
  remoteConfigs: remoteConfigsApi,
  userAgent,
  pageQueryParamMap,
}: MyAppProps) {
  const { setConfigs } = useRemoteConfigState();
  const { isUserLoggedIn, setIsUserLoggedIn } = useUserState();
  const { showSpinner, setShowSpinner } = useSpinnerState();
  const { setBadges, cleanupNotificationStore } = useNotificationState();
  const { setShowOTPModal } = useOTPModalState();
  useInterceptors();
  reportAccessibility();
  useAdminCredentialByPass();
  localizationService.setServicesLocales(router.locale as Locale);
  const { getQueryParam } = useQueryParams();
  const [isWebView, setIsWebView] = useState<boolean>(false);
  const [alternateHrefs, setAlternateHrefs] = useState<JSX.Element[]>([]);
  const { setFavoritedVehicleIds, setFavoritedExperienceIds } =
    useFavoriteStore();

  const getFavoritedVehicleIds = async () => {
    if (isUserLoggedIn) {
      const [favoritesVehicle, favoritesExperience] = await Promise.allSettled([
        favoriteService.getAllFavorites(),
        favoriteService.getAllExperienceFavorites(),
      ]);

      if (favoritesVehicle.status === 'fulfilled') {
        setFavoritedVehicleIds(favoritesVehicle.value.map((f) => f.vehicleId));
      }

      if (favoritesExperience.status === 'fulfilled') {
        setFavoritedExperienceIds(favoritesExperience.value.map((f) => f.id));
      }

      return;
    }
    setFavoritedVehicleIds([]);
    setFavoritedExperienceIds([]);
  };

  const { checkIsMobile, setIsMobile } = useBreakpoint();

  const checkIfWebView = () => {
    // webview check
    let sessionIsWebView = false;
    const isCSR = typeof window !== 'undefined';
    if (isCSR && window.sessionStorage.getItem('isWebView') === 'true') {
      sessionIsWebView = true;
    }
    const isCurrentSessionWebView =
      getQueryParam('isWebView') === 'true' || sessionIsWebView;
    setIsWebView(isCurrentSessionWebView);
    if (isCSR) {
      window.sessionStorage.setItem(
        'isWebView',
        isCurrentSessionWebView.toString()
      );
    }
  };

  const setSlugTranslations = () => {
    const { slugTranslations } = pageProps as any;
    localizationService.slugTranslations = { ...slugTranslations };
  };

  useFirstRender(() => {
    setIsMobile(checkIsMobile(userAgent));
    checkIfWebView();
    setConfigs(remoteConfigsApi || {});
    setAlternateHrefs(getAlternateHref(router, pageQueryParamMap));
    setSlugTranslations();
  });

  const refetchSearchData = () => {
    if (searchDataApi.length === 0) {
      searchService
        .getAllSearchResults(router.locale as Locale)
        .then((searchData) => {
          setAllSearchData(searchData);
          setSearchLocale(router.locale as Locale);
        });
    }
  };

  const refetchRemoteConfigs = () => {
    if (Object.keys(remoteConfigsApi).length === 0) {
      remoteConfigService.getConfigs().then((remoteConfigs) => {
        setConfigs(remoteConfigs || {});
      });
    }
  };

  useComponentMounted(() => {
    refetchSearchData();
    refetchRemoteConfigs();
    setIsUserLoggedIn();

    const closeSpinner = () => {
      setShowSpinner(false);
    };
    router.events.on('routeChangeStart', closeSpinner);
    return () => router.events.off('routeChangeStart', closeSpinner);
  });

  useEffect(() => {
    setAlternateHrefs(getAlternateHref(router, pageQueryParamMap));
  }, [router.asPath, router.locale, router]);

  const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV;

  const { allSearchData, setAllSearchData, searchLocale, setSearchLocale } =
    useSearchState();

  if (!allSearchData?.length || searchLocale !== router.locale) {
    setAllSearchData(searchDataApi);
    setSearchLocale(router.locale as Locale);
  }

  const setAllSearchDataExp = useExperienceSearchState(
    (s) => s.setAllSearchData
  );
  const setSearchLocaleExp = useExperienceSearchState((s) => s.setSearchLocale);
  const allSearchDataExp = useExperienceSearchState((s) => s.allSearchData);
  const searchLocaleExp = useExperienceSearchState((s) => s.searchLocale);

  if (!allSearchDataExp?.length || searchLocaleExp !== router.locale) {
    setAllSearchDataExp(experienceSearchDataApi);
    setSearchLocaleExp(router.locale as Locale);
  }

  usePrevLink();

  useFirstRender(() => {
    analyticsService.userInfo();
  });

  useEffect(() => {
    getFavoritedVehicleIds();
    if (isUserLoggedIn) {
      notificationService.getNotifications().then(setBadges);
      authService.isUserVerifiedOTP().then((isUserVerified) => {
        if (!isUserVerified) {
          setShowOTPModal(true);
        }
      });
    } else {
      cleanupNotificationStore();
      authService.removeOTPCookie();
    }
  }, [isUserLoggedIn]);
  const LayoutComponent: FunctionComponent<LayoutProps> = Layout;

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        ></meta>
        {APP_ENV === 'prod' ? <Partytown debug={false} /> : null}
        {APP_ENV === 'prod' ? (
          <>
            <script
              key="hotjar"
              type="text/partytown"
              dangerouslySetInnerHTML={{
                __html: `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:2970193,hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
              }}
            />
          </>
        ) : null}
        {alternateHrefs}
      </Head>
      <LayoutComponent isWebView={isWebView}>
        <Component {...pageProps} />
      </LayoutComponent>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        rtl={false}
        theme="colored"
        pauseOnFocusLoss
        draggable
        transition={Slide}
        pauseOnHover
      />
      <AuthModalWrapper />
      <OTPModal />
      {showSpinner ? <BSpinner /> : null}
      <NextNProgress
        color="var(--clr-primary-500)"
        height={4}
        showOnShallow={false}
        options={{ easing: 'ease', speed: 500, showSpinner: false }}
      />
    </>
  );
}

MyApp.getInitialProps = async function (appContext: AppContext) {
  const appProps = await NextApp.getInitialProps(appContext);

  const pageQueryParamMapPromise = (async () => {
    const map: Record<string, string> = {};
    if (appContext.router.locales) {
      for (const locale of appContext.router.locales) {
        const t = await getT(locale, 'common');
        map[locale] = t('pageQueryParam');
      }
    }
    return map;
  })();

  const [
    searchData,
    remoteConfigs,
    locationData,
    experienceSearchData,
    pageQueryParamMap,
  ] = await Promise.allSettled([
    searchService.getAllSearchResults(
      (appContext.ctx.locale as Locale) || Locale.TR
    ),
    remoteConfigService.getConfigs(),
    locationService.getLocationLinkSlugTranslations(),
    searchService.getAllExperienceSearchResults(
      (appContext.ctx.locale as Locale) || Locale.TR
    ),
    pageQueryParamMapPromise,
  ]);

  if (locationData.status === 'fulfilled') {
    locationService.setLocationLinks(locationData.value);
  }

  return {
    ...appProps,
    searchData: searchData.status === 'fulfilled' ? searchData.value : [],
    experienceSearchData:
      experienceSearchData.status === 'fulfilled'
        ? experienceSearchData.value
        : [],
    remoteConfigs:
      remoteConfigs.status === 'fulfilled' ? remoteConfigs.value : {},
    userAgent: appContext.ctx.req?.headers['user-agent'] || '',
    pageQueryParamMap:
      pageQueryParamMap.status === 'fulfilled' ? pageQueryParamMap.value : {},
  };
};

const __Page_Next_Translate__ = withTranslateRoutes(JSON.parse(`{"debug":false,"defaultLocale":"tr","locales":["en","tr"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"app","paths":{"default":"app"}},{"name":"basinda-biz","paths":{"default":"basinda-biz","en":"press-media"}},{"name":"favorilerim","paths":{"default":"favorilerim","en":"my-favorites"}},{"name":"gizlilik","paths":{"default":"gizlilik","en":"privacy"}},{"name":"hakkimizda","paths":{"default":"hakkimizda","en":"about"}},{"name":"iletisim","paths":{"default":"iletisim","en":"contact"}},{"name":"index","paths":{"default":"index"}},{"name":"kampanyalar","paths":{"default":"kampanyalar","en":"campaigns"}},{"name":"kisisel-bilgilerim","paths":{"default":"kisisel-bilgilerim","en":"profile"}},{"name":"message","paths":{"default":"message"}},{"name":"partner","paths":{"default":"partner"}},{"name":"products.xml","paths":{"default":"products.xml"}},{"name":"rezervasyonlarim","paths":{"default":"rezervasyonlarim","en":"my-reservations"}},{"name":"tekliflerim","paths":{"default":"tekliflerim","en":"my-offers"}},{"name":"hizmetler","paths":{"default":"hizmetler","en":"services"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"legal","paths":{"default":"legal","en":"legal"},"children":[{"name":"[slug]","paths":{"default":":slug","en":":slug"}}]},{"name":"payment","paths":{"default":"payment"},"children":[{"name":"[id]","paths":{"default":":id"}},{"name":"experience","paths":{"default":"experience"},"children":[{"name":"[id]","paths":{"default":":id"}}]}]},{"name":"tekne-turlari","paths":{"default":"tekne-turlari","en":"boat-tours"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"tekneler","paths":{"default":"tekneler","en":"boats"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"yardim-merkezi","paths":{"default":"yardim-merkezi","en":"help-center"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[userTypeSlug]","paths":{"default":":userTypeSlug"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[categorySlug]","paths":{"default":":categorySlug"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[articleSlug]","paths":{"default":":articleSlug"},"children":[{"name":"index","paths":{"default":"index"}}]}]}]}]},{"name":"[category]","paths":{"default":":category","en":":category"},"children":[{"name":"index","paths":{"default":"index"}}]}]}}`), MyApp);


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: false,
// @ts-ignore
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
    });
// @ts-ignore
  